import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

const fallback = [
  {
    id: "p-01",
    title: "Sunburst Dashboard",
    client: "Beam Analytics",
    tag: "SaaS / Data",
    year: "2025",
    summary: "Realtime analytics dashboard with a punchy yellow UI and bento layout.",
    image:
      "https://static.prod-images.emergentagent.com/jobs/e3717092-a4c0-4867-b3a5-ffe70e23e7f2/images/a95fb091fce09ed47f9efd80dd82eaff93e1ac3c3360136f6184cd395024c35e.png",
    color: "#FFD800",
  },
];

export default function Projects({ projects = [] }) {
  const list = projects.length ? projects : fallback;

  return (
    <section
      id="work"
      data-testid="projects-section"
      className="relative py-24 sm:py-32 px-4 sm:px-6 bg-white border-y-[3px] border-[#0D0D0D]"
    >
      <div className="absolute inset-0 paper-grid pointer-events-none opacity-50" />

      <div className="relative mx-auto max-w-7xl">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <span className="font-mono text-xs uppercase tracking-[0.3em] text-[#4A4A4A]">
              [02] — Selected Work
            </span>
            <h2 className="mt-3 font-display font-black uppercase text-5xl sm:text-6xl lg:text-7xl leading-[0.9] tracking-tighter">
              Built with <br />
              <span className="font-script normal-case text-[#FF0055] text-6xl sm:text-7xl lg:text-8xl tracking-normal">
                love & deadlines.
              </span>
            </h2>
          </div>
          <p className="max-w-sm font-body text-[#4A4A4A]">
            A peek at recent collaborations. Click any project — case studies on
            request.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
          {list.map((p, idx) => (
            <motion.a
              key={p.id}
              href={p.url || "#contact"}
              target={p.url ? "_blank" : undefined}
              rel={p.url ? "noopener noreferrer" : undefined}
              data-testid={`project-card-${idx}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.55, delay: idx * 0.08 }}
              className={`group relative block brut-border brut-shadow-lg overflow-hidden bg-[#FDFBF7] transition-transform ${
                idx % 2 === 1 ? "md:mt-12" : ""
              }`}
              style={{ background: p.color }}
            >
              <div className="relative aspect-[4/3] overflow-hidden border-b-[3px] border-[#0D0D0D]">
                <img
                  src={p.image}
                  alt={p.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute top-3 left-3 px-2.5 py-1 bg-[#0D0D0D] text-[#FDFBF7] font-mono text-[10px] uppercase tracking-widest brut-border">
                  {p.tag}
                </div>
                <div className="absolute top-3 right-3 px-2.5 py-1 bg-[#FDFBF7] text-[#0D0D0D] font-mono text-[10px] uppercase tracking-widest brut-border">
                  {p.year}
                </div>
              </div>
              <div className="p-5 sm:p-6 bg-white flex items-start justify-between gap-4">
                <div>
                  <div className="font-mono text-[11px] uppercase tracking-widest text-[#4A4A4A]">
                    {p.client}
                  </div>
                  <h3 className="mt-1 font-display font-black uppercase text-2xl sm:text-3xl leading-tight">
                    {p.title}
                  </h3>
                  <p className="mt-2 text-sm text-[#4A4A4A] max-w-md">{p.summary}</p>
                </div>
                <div className="shrink-0 w-11 h-11 brut-border bg-[#FFD800] flex items-center justify-center group-hover:rotate-45 transition-transform">
                  <ArrowUpRight size={18} />
                </div>
              </div>
            </motion.a>
          ))}
        </div>
      </div>
    </section>
  );
}
